import { UseMutationOptions, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { NotificationTypes } from '../../enums/notificationTypes';
import {
  DeleteWorkOrderAttachmentPayload,
  DeleteWorkOrderAttachmentResponse,
  DeleteWorkOrderResponse,
  EditWorkOrderPayload,
  UpdateWorkOrderStatusPayload,
  UpdateWorkOrderStatusResponse,
  WorkOrderDetails,
  WorkOrderPayload,
  WorkOrdersParams,
  WorkOrdersResponse,
} from '../../models/assetWorkOrder.model';
import type { WorkOrderSettings } from '../../types/InfrastructureManagerTypes';
import {
  fetchWorkOrders,
  fetchAllWorkOrders,
  fetchWorkOrder,
  fetchWorkOrderSettings,
  saveWorkOrderSettings,
  createWorkOrder,
  saveWorkOrder,
  saveWorkOrderStatus,
  removeWorkOrder,
  removeWorkOrderAttachment,
  uploadWorkOrderFile,
  notifyWorkOrderService,
} from './workOrderServiceUtils';
import { getAssetManagerErrorMessage, retryUnlessClientError } from '../AssetManager/assetManager.service';
import { workOrderServiceMessages, workOrderServiceNotificationKeys } from './workOrder.constants';

const useWorkOrderService = () => {
  const queryClient = useQueryClient();

  const invalidateWorkOrders = () => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_WORK_ORDERS] });
  const invalidateWorkOrder = () => queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_WORK_ORDER] });
  const invalidateAll = () => Promise.all([invalidateWorkOrders(), invalidateWorkOrder()]);

  const onMutationError = (error: Error) => notifyWorkOrderService(NotificationTypes.ERROR, getAssetManagerErrorMessage(error));

  const getWorkOrders = (params: WorkOrdersParams = {}): UseQueryOptions<WorkOrdersResponse, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_WORK_ORDERS, params],
    queryFn: ({ signal }) => fetchWorkOrders(params, signal),
    retry: retryUnlessClientError,
  });

  // Same key prefix as getWorkOrders, so work-order mutations invalidate it too.
  const getAllWorkOrders = (params: Omit<WorkOrdersParams, 'limit' | 'offset'> = {}): UseQueryOptions<WorkOrdersResponse, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_WORK_ORDERS, 'all', params],
    queryFn: ({ signal }) => fetchAllWorkOrders(params, signal),
    retry: retryUnlessClientError,
  });

  const getWorkOrderSettings = (): UseQueryOptions<WorkOrderSettings, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_WORK_ORDER_SETTINGS],
    queryFn: ({ signal }) => fetchWorkOrderSettings(signal),
    staleTime: Infinity,
    retry: retryUnlessClientError,
  });

  // Applied to the cache at once so toggles and colours respond without waiting on the save.
  const saveSettings = (): UseMutationOptions<WorkOrderSettings, Error, WorkOrderSettings> => ({
    mutationKey: [MutationKeys.SAVE_ASSET_WORK_ORDER_SETTINGS],
    mutationFn: saveWorkOrderSettings,
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.GET_ASSET_WORK_ORDER_SETTINGS] });
      queryClient.setQueryData([QueryKeys.GET_ASSET_WORK_ORDER_SETTINGS], next);
    },
    onSuccess: (saved) => {
      // An earlier save finishing must not undo a later change still in flight.
      if (queryClient.isMutating({ mutationKey: [MutationKeys.SAVE_ASSET_WORK_ORDER_SETTINGS] }) > 1) return;
      queryClient.setQueryData([QueryKeys.GET_ASSET_WORK_ORDER_SETTINGS], saved);
    },
    onError: () => {
      // Back to what the server holds.
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_WORK_ORDER_SETTINGS] });
      notifyWorkOrderService(NotificationTypes.ERROR, workOrderServiceMessages.SAVE_SETTINGS_ERROR);
    },
  });

  // A null id (create mode) keeps the query idle.
  const getWorkOrder = (id: number | null): UseQueryOptions<WorkOrderDetails, Error> => ({
    queryKey: [QueryKeys.GET_ASSET_WORK_ORDER, id],
    queryFn: ({ signal }) => fetchWorkOrder(id as number, signal),
    enabled: id !== null,
    retry: retryUnlessClientError,
  });

  const addWorkOrder = (): UseMutationOptions<WorkOrderDetails, Error, WorkOrderPayload> => ({
    mutationKey: [MutationKeys.CREATE_ASSET_WORK_ORDER],
    mutationFn: createWorkOrder,
    onSuccess: () => {
      invalidateWorkOrders();
      // One toast when a multi-date submit creates several work orders.
      notifyWorkOrderService(NotificationTypes.SUCCESS, workOrderServiceMessages.CREATE_SUCCESS, workOrderServiceNotificationKeys.CREATE_SUCCESS);
    },
    onError: onMutationError,
  });

  const editWorkOrder = (): UseMutationOptions<WorkOrderDetails, Error, EditWorkOrderPayload> => ({
    mutationKey: [MutationKeys.EDIT_ASSET_WORK_ORDER],
    mutationFn: saveWorkOrder,
    onSuccess: () => {
      invalidateAll();
      notifyWorkOrderService(NotificationTypes.SUCCESS, workOrderServiceMessages.EDIT_SUCCESS);
    },
    onError: onMutationError,
  });

  const updateWorkOrderStatus = (): UseMutationOptions<UpdateWorkOrderStatusResponse, Error, UpdateWorkOrderStatusPayload> => ({
    mutationKey: [MutationKeys.UPDATE_ASSET_WORK_ORDER_STATUS],
    mutationFn: saveWorkOrderStatus,
    onSuccess: (data) => {
      invalidateAll();
      notifyWorkOrderService(NotificationTypes.SUCCESS, data.message);
    },
    onError: onMutationError,
  });

  // Resolves to the S3 key the file was uploaded under; the upload helper reports its own failure.
  const uploadAttachment = (): UseMutationOptions<string, Error, File> => ({
    mutationKey: [MutationKeys.UPLOAD_ASSET_WORK_ORDER_ATTACHMENT],
    mutationFn: uploadWorkOrderFile,
  });

  const deleteWorkOrder = (): UseMutationOptions<DeleteWorkOrderResponse, Error, number> => ({
    mutationKey: [MutationKeys.DELETE_ASSET_WORK_ORDER],
    mutationFn: removeWorkOrder,
    onSuccess: (data) => {
      invalidateWorkOrders();
      notifyWorkOrderService(NotificationTypes.SUCCESS, data.message);
    },
    onError: onMutationError,
  });

  const deleteWorkOrderAttachment = (): UseMutationOptions<DeleteWorkOrderAttachmentResponse, Error, DeleteWorkOrderAttachmentPayload> => ({
    mutationKey: [MutationKeys.DELETE_ASSET_WORK_ORDER_ATTACHMENT],
    mutationFn: removeWorkOrderAttachment,
    onSuccess: (data) => {
      invalidateAll();
      notifyWorkOrderService(NotificationTypes.SUCCESS, data.message);
    },
    onError: onMutationError,
  });

  return {
    getWorkOrders,
    getAllWorkOrders,
    getWorkOrder,
    getWorkOrderSettings,
    saveSettings,
    addWorkOrder,
    editWorkOrder,
    updateWorkOrderStatus,
    uploadAttachment,
    deleteWorkOrder,
    deleteWorkOrderAttachment,
  };
};

export default useWorkOrderService;
